import React, { useMemo } from 'react';
import { ArrowLeft, Battery, Boxes, MapPin, Radio, Smartphone, Wrench } from 'lucide-react';
import { useAgriSafeSoverdiDashboard } from '../hooks/useAgriSafeSoverdiDashboard';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { useNodeHistory } from '../../hooks/useNodeHistory';
import { isNodeOnline, nodeTimestamp } from '../../lib/operationalOverview';
import { getNodeBattery } from '../../lib/liveSensorMetrics';
import {
  equipmentCommissioning,
  equipmentMetrics,
  equipmentType,
  formatEquipmentValue,
} from '../adapters/equipmentPresentation';

const ageText = (timestamp: number | null): string => {
  if (timestamp === null) return 'Non remontée';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `il y a ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${Math.floor(hours / 24)} j`;
};

const nodeIdFromPath = (): string => {
  const prefix = '/r6-soverdi-equipment/';
  const pathname = window.location.pathname;
  return pathname.startsWith(prefix) ? decodeURIComponent(pathname.slice(prefix.length)) : '';
};

const SoverdiEquipmentDetailR7: React.FC = () => {
  const { topology, nodes, placement, siteModels, loading } = useAgriSafeSoverdiDashboard();
  const { parcelBindings, pumps, error: bindingsError } = useFieldBindings();
  const nodeId = nodeIdFromPath();
  const node = nodes.find((candidate) => candidate.id === nodeId);
  const history = useNodeHistory(node?.id ?? null, 24);

  const context = useMemo(() => {
    if (!node) return { siteName: '', sectorCode: '' };
    if (node.sector_id) {
      const located = placement.get(node.sector_id);
      if (located) return located;
    }
    for (const site of siteModels) {
      if (site.relayNode?.id === node.id) return { siteName: site.siteName, sectorCode: '' };
    }
    return { siteName: '', sectorCode: '' };
  }, [node, placement, siteModels]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;
  }

  if (!topology || !node) {
    return (
      <div className="space-y-4">
        <a href="/r6-soverdi-equipment" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]"><ArrowLeft className="h-4 w-4" />Retour aux équipements</a>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-4 py-12 text-center text-sm text-[var(--text-secondary)]">Équipement indisponible</div>
      </div>
    );
  }

  const online = isNodeOnline(node, Date.now());
  const commissioning = equipmentCommissioning(node, parcelBindings, pumps);
  const metrics = equipmentMetrics(node);
  const battery = getNodeBattery(node) ?? node.battery_v ?? undefined;
  const timestamp = nodeTimestamp(node);
  const firstHistoryTs = history.series.find((point) => point.ts)?.ts ?? null;
  const lastHistoryTs = [...history.series].reverse().find((point) => point.ts)?.ts ?? null;

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="agrisafe-equipment-detail-r7">
      <a href="/r6-soverdi-equipment" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]"><ArrowLeft className="h-4 w-4" />Retour aux équipements</a>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2 text-[var(--color-primary)]"><Boxes className="h-5 w-5" /><span className="text-sm font-medium">Fiche équipement</span></div>
          <h1 className="truncate text-xl font-semibold text-[var(--text-primary)] sm:text-2xl" title={node.name}>{node.name}</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{topology.clientName}{context.siteName ? ` · ${context.siteName}` : ''}{context.sectorCode ? ` · Secteur ${context.sectorCode}` : ''}</p>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${online ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)]'}`}>
          <Radio className="h-3.5 w-3.5" />{online ? 'En ligne' : 'Non récent'}
        </div>
      </header>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">Type</div>
          <div className="mt-1 font-semibold text-[var(--text-primary)]">{equipmentType(node)}</div>
          <div className="mt-1 text-xs text-[var(--text-tertiary)]">Modèle {node.model || '—'}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">Commissioning</div>
          <div className="mt-1 font-semibold text-[var(--text-primary)]">{commissioning.label}</div>
          <div className="mt-1 text-xs text-[var(--text-tertiary)]">{bindingsError ? 'Données commissioning indisponibles' : commissioning.detail ?? '—'}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">Dernière communication</div>
          <div className="mt-1 font-semibold text-[var(--text-primary)]">{ageText(timestamp)}</div>
          <div className="mt-1 truncate text-xs text-[var(--text-tertiary)]" title={node.last_seen_at ?? node.latest?.ts ?? ''}>{node.last_seen_at ?? node.latest?.ts ?? '—'}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">Batterie</div>
          <div className="mt-1 flex items-center gap-2 font-semibold text-[var(--text-primary)]"><Battery className="h-4 w-4" />{formatEquipmentValue(battery, 1, 'V')}</div>
          <div className="mt-1 text-xs text-[var(--text-tertiary)]">Valeur réelle si remontée</div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-3">
          <h2 className="font-semibold text-[var(--text-primary)]">Identification & affectation</h2>
        </div>
        <dl className="grid gap-x-6 gap-y-4 p-4 text-sm md:grid-cols-2 xl:grid-cols-4">
          <div><dt className="text-xs text-[var(--text-tertiary)]">ID équipement</dt><dd className="mt-1 break-all font-medium text-[var(--text-primary)]">{node.id}</dd></div>
          <div><dt className="text-xs text-[var(--text-tertiary)]">Exploitation</dt><dd className="mt-1 font-medium text-[var(--text-primary)]">{topology.clientName}</dd></div>
          <div><dt className="text-xs text-[var(--text-tertiary)]">Site</dt><dd className="mt-1 font-medium text-[var(--text-primary)]">{context.siteName || 'Non affecté'}</dd></div>
          <div><dt className="text-xs text-[var(--text-tertiary)]">Secteur</dt><dd className="mt-1 font-medium text-[var(--text-primary)]">{context.sectorCode ? `Secteur ${context.sectorCode}` : 'Non applicable / non affecté'}</dd></div>
        </dl>
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-3">
          <h2 className="font-semibold text-[var(--text-primary)]">Dernières mesures</h2>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">Uniquement les mesures réelles interprétables pour ce modèle.</p>
        </div>
        {metrics.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[var(--text-tertiary)]">Aucune mesure canonique remontée</div>
        ) : (
          <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {metrics.map((metric) => (
              <div key={`${metric.label}-${metric.value}`} className="rounded-lg bg-[var(--bg-tertiary)] p-3">
                <div className="text-xs text-[var(--text-tertiary)]">{metric.label}</div>
                <div className="mt-1 font-semibold text-[var(--text-primary)]">{metric.value}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-3">
          <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">Historique 24 h</h2></div>
        </div>
        <div className="grid gap-3 p-4 sm:grid-cols-3">
          <div className="rounded-lg bg-[var(--bg-tertiary)] p-3"><div className="text-xs text-[var(--text-tertiary)]">Échantillons</div><div className="mt-1 font-semibold text-[var(--text-primary)]">{history.loading ? '…' : history.error ? '—' : history.series.length}</div></div>
          <div className="rounded-lg bg-[var(--bg-tertiary)] p-3"><div className="text-xs text-[var(--text-tertiary)]">Premier point disponible</div><div className="mt-1 text-sm font-medium text-[var(--text-primary)]">{firstHistoryTs || '—'}</div></div>
          <div className="rounded-lg bg-[var(--bg-tertiary)] p-3"><div className="text-xs text-[var(--text-tertiary)]">Dernier point disponible</div><div className="mt-1 text-sm font-medium text-[var(--text-primary)]">{lastHistoryTs || '—'}</div></div>
        </div>
        {history.error && <div className="px-4 pb-4 text-xs text-[var(--text-tertiary)]">Historique temporairement indisponible. Aucune donnée n’est fabriquée.</div>}
      </section>

      <div className="flex items-center justify-center gap-2 text-xs text-[var(--text-tertiary)]"><MapPin className="h-3.5 w-3.5" />Données réelles Agri Safe · lecture seule</div>
    </div>
  );
};

export default SoverdiEquipmentDetailR7;
